import { useEffect, useState } from 'react';
import { WBBox } from '@admiin-com/ds-web';
import { DATE_FORMATS } from '@admiin-com/ds-common';
import { DateTime } from 'luxon';
import * as Handlebars from 'handlebars';
import { registerHandlebarsHelpers } from '../../helpers/handlebars';
import { restoreHandlebarsBlockPlaceholders } from '../../helpers/handlebarsPlaceholders';

/**
 * Import shared document content styles for consistent rendering between editor and preview.
 * These styles ensure WYSIWYG consistency with DocumentEditor.
 */
import '../../features/components/DocumentEditor/document-content.css';

export interface DocumentPreviewData {
  task: any;
  from: any;
  to: any;
  partner?: any;
  tasks?: any[];
  dates?: {
    today: string;
    year: string;
    month: string;
  };
}

const preprocessTemplate = (html: string): string =>
  restoreHandlebarsBlockPlaceholders(html)
    .replace(
      /{{from\.logoUrl}}/g,
      '<img src="{{from.logoUrl}}" alt="{{from.name}} Logo" style="max-width:144px; max-height:96px; object-fit:contain;" />'
    )
    .replace(
      /{{partner\.signatureUrl}}/g,
      '<img src="{{partner.signatureUrl}}" alt="Signature" style="max-width:300px; max-height:60px; object-fit:contain;" />'
    );

const compileTemplate = async (
  templateSource: string,
  data: any
): Promise<string> => {
  registerHandlebarsHelpers();
  const template = Handlebars.compile(templateSource);
  return template(data);
};

interface Props {
  values: DocumentPreviewData;
  customHtml?: string;
}

/**
 * DocumentRenderer renders document HTML with Handlebars template processing.
 *
 * Applies shared document-content styles to ensure consistent typography, spacing,
 * and whitespace handling with the DocumentEditor for WYSIWYG consistency.
 */
const DocumentRenderer = ({ values, customHtml }: Props) => {
  const [compiledHtml, setCompiledHtml] = useState('');

  useEffect(() => {
    const data = {
      ...values,
      dates: {
        today: DateTime.now().toFormat(DATE_FORMATS.USER_DATE),
        year: DateTime.now().toFormat('yyyy'),
        month: DateTime.now().toFormat('MMMM'),
      },
    };

    const html = preprocessTemplate(customHtml ?? '');
    compileTemplate(html, data).then(setCompiledHtml);
  }, [values, customHtml]);

  return (
    <WBBox backgroundColor="#FFF" color="#000" width="100%">
      <WBBox
        backgroundColor="#FFF"
        width="100%"
        className="document-content"
        sx={{
          padding: '0px',
          '& > div': {
            isolation: 'isolate',
          },
          '& .invoice-root *': {
            all: 'revert',
          },
        }}
        dangerouslySetInnerHTML={{ __html: compiledHtml }}
      />
    </WBBox>
  );
};

export default DocumentRenderer;
